/**
 * **The rail fold's moves** (Rebuild 34–36; James, Oct 15). Every address Money had before the
 * fold, and where it lives now: an entry that kept its screen moves to its new section; a folded
 * screen becomes a **part** of its new home, opened as `?part=<its old name>` — the same screen,
 * every button it had, under its home's strip of parts. Old addresses redirect here, so a link in
 * an email, a bookmark or a walk still lands.
 */

/** Old `section/screen` → the new home and, for a folded screen, the part it became. */
export interface Move {
  /** The new `section/screen`. */
  to: string;
  /** The part's name in `?part=`, for a folded screen. */
  part?: string;
  /** The part's label on its home's strip. */
  label?: string;
}

const m = (to: string, part?: string, label?: string): Move =>
  part ? { to, part, label: label ?? part } : { to };

export const MOVES: Readonly<Record<string, Move>> = {
  // Today
  'today/standing': m('today/standing'),
  'today/director': m('today/director'),
  // Budget → Ask · Approve
  'budget/home': m('ask-approve/home'),
  'budget/history': m('ask-approve/history'),
  'budget/festival': m('ask-approve/festival'),
  'budget/area': m('ask-approve/area'),
  'budget/review': m('ask-approve/review'),
  'budget/committed': m('ask-approve/committed'),
  'budget/budget': m('ask-approve/committed', 'budget', 'Budget'),
  'budget/forecast': m('ask-approve/forecast'),
  // Rebuild 46 bud11: who owns what — two roles across, accounts down.
  'budget/owners': m('ask-approve/owners'),
  'budget/outcome': m('ask-approve/forecast', 'outcome', 'Outcome'),
  'close-and-reports/accounts': m('ask-approve/accounts'),
  // Rebuild 46 unmapped and rekey: each its own page, parts of Accounts (Oct 30).
  'close-and-reports/unmapped': m('ask-approve/accounts', 'unmapped', 'Map what came in'),
  'close-and-reports/rekey': m('ask-approve/accounts', 'rekey', 'Re-key onto v3'),
  // Bills & checks → Spend · Pay
  'bills-and-checks/worklist': m('spend-pay/worklist'),
  'bills-and-checks/line-card': m('spend-pay/line-card'),
  'bills-and-checks/drafts': m('spend-pay/worklist', 'drafts', 'Drafts'),
  'bills-and-checks/incoming': m('spend-pay/worklist', 'incoming', 'Incoming'),
  'bills-and-checks/chair-expenses': m('spend-pay/worklist', 'chair-expenses', 'Chair expenses'),
  'bills-and-checks/purchase-orders': m('spend-pay/purchase-orders'),
  'bills-and-checks/bills-to-pay': m('spend-pay/bills-to-pay'),
  'bills-and-checks/print-checks': m('spend-pay/print-checks'),
  'bills-and-checks/small-expenses-and-mileage': m('spend-pay/small-expenses-and-mileage'),
  'bills-and-checks/vendors': m('spend-pay/vendors'),
  'bills-and-checks/recurring': m('close-report/finance-year', 'recurring', 'Recurring'),
  // Sales & Square → Sell · Count
  'sales-and-square/live-sales': m('sell-count/live-sales'),
  'sales-and-square/register-health': m(
    'sell-count/live-sales',
    'register-health',
    'Register health',
  ),
  'sales-and-square/drawer-check': m('sell-count/live-sales', 'drawer-check', 'Drawer check'),
  'sales-and-square/item-mapping': m('sell-count/item-mapping'),
  'sales-and-square/products': m('sell-count/item-mapping', 'products', 'Products'),
  // Rebuild 36's Items by margin (sq06; James, Oct 15): new with the fold, no old address.
  'sell-count/items-by-margin': m('sell-count/items-by-margin'),
  'sales-and-square/on-hand': m('sell-count/on-hand'),
  'sales-and-square/orders': m('sell-count/orders'),
  'sales-and-square/settlement-by-product': m('sell-count/settlement-by-product'),
  'sales-and-square/receivables': m('sell-count/receivables'),
  'sales-and-square/donations-and-funds': m('sell-count/donations-and-funds'),
  // Cash & bank → Cash · Bank
  'cash-and-bank/count-room': m('cash-bank/count-room'),
  'cash-and-bank/count-room-desk': m('cash-bank/count-room', 'count-room-desk', 'Count-room desk'),
  'cash-and-bank/seed-bank': m('cash-bank/count-room', 'seed-bank', 'Seed bank'),
  'cash-and-bank/pickup-route': m('cash-bank/count-room', 'pickup-route', 'Pickup route'),
  'cash-and-bank/count-station': m('cash-bank/count-room', 'count-station', 'Count station'),
  'cash-and-bank/count-queue': m('cash-bank/count-room', 'count-queue', 'Count queue'),
  'cash-and-bank/register-reconciliation': m(
    'cash-bank/count-room',
    'register-reconciliation',
    'Register reconciliation',
  ),
  'cash-and-bank/deposit-builder': m('cash-bank/count-room', 'deposit-builder', 'Deposit builder'),
  'cash-and-bank/bag-registry': m('cash-bank/count-room', 'bag-registry', 'Bag registry'),
  'cash-and-bank/chain-of-custody': m(
    'cash-bank/count-room',
    'chain-of-custody',
    'Chain of custody',
  ),
  'cash-and-bank/day-seal': m('cash-bank/count-room', 'day-seal', 'Day seal'),
  'cash-and-bank/count-room-settings': m(
    'cash-bank/count-room',
    'count-room-settings',
    'Count-room settings',
  ),
  'cash-and-bank/bank-match': m('cash-bank/bank-match'),
  'cash-and-bank/rules': m('cash-bank/bank-match', 'rules', 'Rules'),
  // The cash strip (James, Oct 15): its own screen, and the three it reads become its parts.
  'cash-bank/cash-strip': m('cash-bank/cash-strip'),
  'cash-and-bank/seed-bags': m('cash-bank/cash-strip', 'seed-bags', 'Seed bags'),
  'cash-and-bank/deposits-and-registers': m(
    'cash-bank/cash-strip',
    'deposits-and-registers',
    'Deposits & registers',
  ),
  'cash-and-bank/bank-run': m('cash-bank/cash-strip', 'bank-run', 'Bank run'),
  // Close & reports and Distributions → Close · Report
  'close-and-reports/finance-year': m('close-report/finance-year'),
  'close-and-reports/state-filings': m(
    'close-report/finance-year',
    'state-filings',
    'State filings',
  ),
  'close-and-reports/month-close': m('close-report/month-close'),
  // The cutover (Rebuild 46; M6): one screen for one month, a part of Closing the month.
  'close-and-reports/cutover': m('close-report/month-close', 'cutover', 'Cutover'),
  // Rebuild 46 closeout: End the year opens on the eleven lines; the 1099s and the accountant’s package
  // (Rebuild 46 yearend) are its part, so an old address to them still lands (Oct 29 night).
  'close-and-reports/ending-year': m('close-report/year-end'),
  'close-and-reports/year-end': m(
    'close-report/year-end',
    'package',
    'The 1099s & the accountant’s package',
  ),
  'distributions/distributions': m('close-report/distributions'),
  'distributions/seed': m('close-report/distributions', 'seed', 'Seed'),
  // Rebuild 46 projects ("Plan a project"): its own page, a part of Send the money out (Oct 29).
  'distributions/projects': m('close-report/distributions', 'projects', 'Projects'),
  'close-and-reports/distribution': m('close-report/distributions', 'distribution', 'Distribution'),
  'close-and-reports/reports': m('close-report/reports'),
  // Rebuild 46 bva: the directors' one-pager, budget vs actual, a part of Reports.
  'close-and-reports/one-pager': m('close-report/reports', 'one-pager', 'The one-pager'),
  'close-and-reports/1099-run': m('close-report/reports', '1099-run', '1099 run'),
  'close-and-reports/sales-tax': m('close-report/reports', 'sales-tax', 'Sales tax'),
  'close-and-reports/journal': m('close-report/journal'),
  'close-and-reports/who-did-what': m('close-report/journal', 'who-did-what', 'Who did what'),
  'close-and-reports/parish-account': m('close-report/journal', 'parish-account', 'Parish account'),
  'close-and-reports/documents': m('close-report/journal', 'documents', 'Documents'),
  'close-and-reports/import': m('close-report/import'),
  'close-and-reports/year-proofs': m('close-report/import', 'year-proofs', 'Year proofs'),
};

/** Addresses the rail had between the fold and the cash strip (Oct 15 evening), by their old key. */
const BETWEEN: Readonly<Record<string, string>> = {
  'cash-bank/seed-bags': 'cash-and-bank/seed-bags',
  'cash-bank/deposits-and-registers': 'cash-and-bank/deposits-and-registers',
  'cash-bank/bank-run': 'cash-and-bank/bank-run',
};

/** The new address for an old one, with its part — or null when it is not an old address. */
export function movedTo(key: string): string | null {
  const oldKey = BETWEEN[key] ?? key;
  const mv = MOVES[oldKey];
  if (!mv) return null;
  const path = `/money/${mv.to}${mv.part ? `?part=${mv.part}` : ''}`;
  return mv.to === key && !mv.part ? null : path;
}

/** Which old screen a new address with its part is — the component that draws it. */
export function screenAt(newKey: string, part: string | null): string | null {
  for (const [old, mv] of Object.entries(MOVES))
    if (mv.to === newKey && (mv.part ?? null) === part) return old;
  return null;
}

/** A home's parts, in order, for its strip. */
export function partsOf(newKey: string): { part: string; label: string }[] {
  return Object.values(MOVES)
    .filter((mv) => mv.to === newKey && mv.part)
    .map((mv) => ({ part: mv.part ?? '', label: mv.label ?? '' }));
}

/** An old section's slug → its new one, for `/money/<section>` on its own. */
const SECTIONS: Readonly<Record<string, string>> = {
  budget: 'ask-approve',
  'bills-and-checks': 'spend-pay',
  'sales-and-square': 'sell-count',
  'cash-and-bank': 'cash-bank',
  'close-and-reports': 'close-report',
  distributions: 'close-report',
};

/**
 * The address an old Money href lands on, keeping the rest of its query string — or null when
 * the href is not an old address. The router rewrites in place, so the back button has one step.
 */
export function moneyMove(href: string): string | null {
  const [path = '', query = ''] = href.split('?');
  const [, view, section, screen] = path.split('/');
  if (view !== 'money' || !section) return null;
  if (!screen) {
    const to = SECTIONS[section];
    return to ? `/money/${to}${query ? `?${query}` : ''}` : null;
  }
  const to = movedTo(`${section}/${screen}`);
  if (!to) return null;
  const extra = query ? `${to.includes('?') ? '&' : '?'}${query}` : '';
  return to + extra;
}

/** Rebuild 37's words and hover reasons for a folded page's home (James, Oct 16). */
export const HOME: Record<string, { label: string; why: string }> = {
  'cash-bank/cash-strip': { label: 'The strip', why: 'every bag, every state, per day' },
  'close-report/import': { label: 'The import', why: 'twenty-three years, once' },
  'spend-pay/worklist': {
    label: 'Work the list',
    why: 'submitted · approved · POs to match · red lines',
  },
  'cash-bank/bank-match': { label: 'Match the bank', why: 'the statement beside our rows' },
};
/** Rebuild 37's words and hover reasons for its parts; elsewhere a part keeps its own name. */
export const PART: Record<string, { label?: string; why: string }> = {
  'seed-bags': { why: 'the August withdrawal, mapped to readers' },
  'deposits-and-registers': { why: 'what Square and the registers say against the bags' },
  'bank-run': { why: 'who carries which bags, when, with the slip' },
  'year-proofs': { why: 'each year balanced against the old file' },
  cutover: { label: 'Cutover', why: 'seven steps from QuickBooks to CIDER, two directors' },
  drafts: { why: 'entries started and not sent' },
  incoming: { why: 'bills that arrived by mail or email, not yet on a line' },
  'chair-expenses': { label: 'Submitted', why: 'what chairs sent from their phones' },
  rules: { why: 'what codes a line on sight · reclassify with a log' },
};
